import { useState } from "react";
import { useEmail } from "../hooks/useEmail";
import { motion } from "framer-motion";
import {
    faPhone,
    faEnvelope,
    faGlobe,
    faMapMarkerAlt,
    faClock,
    faCheckCircle,
    faComments,
    faChartLine,
    faShieldAlt,
    faFileInvoiceDollar,
    faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breadcrumb } from "../components/Breadcrumb";
// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            ease: "easeOut"
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};
const Contact = () => {
    const { sendEmail, isSending } = useEmail();

    // useState to control form values
    const [form, setForm] = useState({
      name: "",
      email: "",
      phone: "",
      city: "",
      topic: "",
      message: "",
    });
  
    // update state on input change
    const handleChange = (e) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: value }));
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      const { name, email, phone, city, topic, message } = form;
  
      const emailMsg = `
        Name: ${name}
        Email: ${email}
        Phone: ${phone}
        City & State: ${city}
        Topic: ${topic}
        Message: ${message}
      `;
  
      const subject = `New Contact Form Submission - ${topic}`;
      try {
        await sendEmail(emailMsg, subject);
      } catch (err) {
        console.error(err);
      } finally {
        // ✅ Always reset form
        setForm({
          name: "",
          email: "",
          phone: "",
          city: "",
          topic: "",
          message: "",
        });
      }
    };

    return (
        <>

            <Breadcrumb currentName={"Contact"} currentParentName={"Contact"} />
            {/* Intro Section */}
            <div className="container py-5">
                <motion.div
                    className="text-center mb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    <motion.h2
                        className="fw-bold mb-4 display-4 gradient-text"
                        variants={fadeInUp}
                        style={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 700,
                            color: 'var(--heading)'
                        }}
                    >
                        Let's Talk About Your Financial Future
                    </motion.h2>

                    <motion.p
                        className="fs-5 mx-auto lead"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "800px",
                            fontFamily: "'Inter', sans-serif",
                            lineHeight: 1.7,
                            color: 'var(--text)'
                        }}
                    >
                        At <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong>, every conversation begins with a
                        question: <em style={{ color: 'var(--heading)', fontStyle: 'italic' }}>What matters most to you?</em> Whether you're starting
                        your financial journey, reviewing investments, or seeking expert
                        advice on insurance, taxes, or retirement — we're here to help you
                        move forward with clarity, confidence, and calm.
                    </motion.p>

                    <motion.p
                        className="fs-6 mx-auto"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "700px",
                            fontFamily: "'Inter', sans-serif",
                            lineHeight: 1.6,
                            color: 'var(--text)'
                        }}
                    >
                        You don't need to know all the answers — that's what we're here for.
                        Let's start with a conversation — simple, honest, and free of
                        pressure.
                    </motion.p>
                </motion.div>

                {/* Reach Out Today */}
                <motion.div
                    className="row g-5 mb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    {/* Left Column - Contact Information */}
                    <motion.div className="col-lg-6" variants={itemVariants}>
                        <div className="card border-0 shadow-lg h-100" style={{ backgroundColor: 'var(--white)' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="fw-bold mb-4 display-6" style={{
                                    fontFamily: "'Playfair Display', serif",
                                    color: 'var(--heading)'
                                }}>
                                    Reach Out Today
                                </h3>
                                <p className="mb-4 fs-5" style={{
                                    fontFamily: "'Inter', sans-serif",
                                    color: 'var(--text)'
                                }}>
                                    You're just one step away from smarter planning and stronger
                                    financial peace of mind.
                                </p>

                                <div className="space-y-3">
                                    {[
                                        { icon: faPhone, text: "Call or WhatsApp:", detail: "+917011468336", link: "tel:+917011468336" },
                                        { icon: faEnvelope, text: "Email:", detail: "contact@sagfinancials.com", link: "mailto:contact@sagfinancials.com" },
                                        { icon: faGlobe, text: "Website:", detail: "www.sagfinancials.com", link: "https://www.sagfinancials.com" },
                                        { icon: faMapMarkerAlt, text: "Location:", detail: "Gurgaon , Haryana — Serving Clients across India & abroad." }
                                    ].map((item, index) => (
                                        <motion.div
                                            key={index}
                                            className="d-flex align-items-start mb-3 p-3 rounded-3 hover-glow"
                                            style={{ backgroundColor: 'var(--light-bg)' }}
                                            whileHover={{ scale: 1.02 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <FontAwesomeIcon
                                                icon={item.icon}
                                                style={{ color: 'var(--primary)' }}
                                                className="mt-1 me-3 fs-5"
                                            />
                                            <div>
                                                <strong style={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    color: 'var(--heading)'
                                                }}>
                                                    {item.text}
                                                </strong>
                                                {item.link ? (
                                                    <a
                                                        href={item.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-none ms-2"
                                                        style={{ color: 'var(--primary)' }}
                                                    >
                                                        {item.detail}
                                                    </a>
                                                ) : (
                                                    <span className="ms-2" style={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        color: 'var(--text)'
                                                    }}>
                                                        {item.detail}
                                                    </span>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <motion.div
                                    className="mt-4 p-3 rounded-3"
                                    style={{ backgroundColor: 'var(--light-bg)' }}
                                    whileHover={{ backgroundColor: 'var(--primary)', color: 'var(--white)' }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon
                                            icon={faClock}
                                            className="me-3 fs-5"
                                            style={{ color: 'var(--primary)' }}
                                        />
                                        <div>
                                            <strong style={{ fontFamily: "'Inter', sans-serif" }}>
                                                Office Hours:
                                            </strong>
                                            <span className="ms-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                                                Mon–Sat: 10:00 AM–6:00 PM | Sunday 10 a.m  to 2 pm
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right Column - Services */}
                    <motion.div className="col-lg-6" variants={itemVariants}>
                        <div className="card border-0 shadow-lg h-100" style={{ backgroundColor: 'var(--white)' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="fw-bold mb-4 display-6" style={{
                                    fontFamily: "'Playfair Display', serif",
                                    color: 'var(--heading)'
                                }}>
                                    How Can We Help You?
                                </h3>
                                <p className="mb-4 fs-5" style={{
                                    fontFamily: "'Inter', sans-serif",
                                    color: 'var(--text)'
                                }}>
                                    We know reaching out to a financial advisor can feel
                                    overwhelming. That's why we've made it simple and comfortable for
                                    you to connect — based on what you need right now:
                                </p>

                                <div className="space-y-3">
                                    {[
                                        {
                                            icon: faChartLine,
                                            title: "I Want to Start Investing",
                                            description: "Request a free investment consultation or our SIP Starter Guide."
                                        },
                                        {
                                            icon: faShieldAlt,
                                            title: "I Need Help with Insurance",
                                            description: "Book a 15-minute review call to assess your coverage."
                                        },
                                        {
                                            icon: faFileInvoiceDollar,
                                            title: "I Need Tax-Saving Advice",
                                            description: "Ask for our Tax-Saver Checklist PDF (free download)."
                                        },
                                        {
                                            icon: faBullseye,
                                            title: "I Want to Plan My Goals",
                                            description: "Schedule a discovery session to map out your top 3 goals."
                                        },
                                        {
                                            icon: faComments,
                                            title: "I Have Questions",
                                            description: "No problem. Send a message below — no pressure, just clarity."
                                        }
                                    ].map((service, index) => (
                                        <motion.div
                                            key={index}
                                            className="p-3 mb-3 border-start border-4 rounded-end hover-lift"
                                            style={{
                                                borderLeftColor: 'var(--primary)',
                                                backgroundColor: 'var(--light-bg)'
                                            }}
                                            whileHover={{
                                                x: 10,
                                                backgroundColor: 'var(--primary)',
                                                color: 'var(--white)'
                                            }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="d-flex align-items-start">
                                                <FontAwesomeIcon
                                                    icon={service.icon}
                                                    className="mt-1 me-3 fs-5"
                                                    style={{ color: 'var(--primary)' }}
                                                />
                                                <div>
                                                    <h6 className="fw-bold mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                                                        {service.title}
                                                    </h6>
                                                    <p className="mb-0 small" style={{ fontFamily: "'Inter', sans-serif" }}>
                                                        {service.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Contact Form */}
            <div className="container-xxl py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
                <div className="container">
                    <div className="text-center mx-auto mb-5" style={{ maxWidth: "700px" }}>
                        <h2 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>Contact Form</h2>
                        <p style={{ color: 'var(--text)' }}>
                            Please fill out the form below and a certified advisor will get
                            back to you within 1 business day. <br />
                            We respect your privacy — your information will never be shared or
                            used for spam.
                        </p>
                    </div>

                    <div className="row g-5">
                        <div className="col-lg-6">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                placeholder="Your Name"
                                                onChange={handleChange}
                                                value={form.name}
                                                required
                                                style={{
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                            />
                                            <label htmlFor="name" style={{ color: 'var(--text)' }}>Your Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                placeholder="Your Email"
                                                value={form.email}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                            />
                                            <label htmlFor="email" style={{ color: 'var(--text)' }}>Your Email</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="tel"
                                                className="form-control"
                                                id="phone"
                                                placeholder="Phone Number"
                                                value={form.phone}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                            />
                                            <label htmlFor="phone" style={{ color: 'var(--text)' }}>Phone Number</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="city"
                                                value={form.city}
                                                onChange={handleChange}
                                                placeholder="City & State"
                                                required
                                                style={{
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                            />
                                            <label htmlFor="city" style={{ color: 'var(--text)' }}>City & State</label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="topic"
                                                onChange={handleChange}
                                                value={form.topic}
                                                required
                                                style={{
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                            >
                                                <option value="">Select Option</option>
                                                <option>Investments</option>
                                                <option>Insurance</option>
                                                <option>Tax Planning</option>
                                                <option>General Inquiry</option>
                                            </select>
                                            <label htmlFor="topic" style={{ color: 'var(--text)' }}>
                                                What do you need help with?
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <textarea
                                                className="form-control"
                                                placeholder="Leave a message here"
                                                id="message"
                                                onChange={handleChange}
                                                value={form.message}
                                                style={{
                                                    height: "100px",
                                                    borderColor: 'var(--primary)',
                                                    backgroundColor: 'var(--white)'
                                                }}
                                                required
                                            ></textarea>
                                            <label htmlFor="message" style={{ color: 'var(--text)' }}>Your Message</label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <button
                                            className="btn py-3 px-5 w-100 fw-bold"
                                            type="submit"
                                            style={{
                                                backgroundColor: 'var(--primary)',
                                                borderColor: 'var(--primary)',
                                                color: 'var(--white)'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.backgroundColor = 'var(--accent)';
                                                e.target.style.borderColor = 'var(--accent)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.backgroundColor = 'var(--primary)';
                                                e.target.style.borderColor = 'var(--primary)';
                                            }}
                                        >
                                            Send Message
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Map or Info */}
                        <div
                            className="col-lg-6 wow fadeIn"
                            data-wow-delay="0.5s"
                            style={{ minHeight: "450px" }}
                        >
                            <div className="position-relative rounded overflow-hidden h-100 shadow-sm">
                                <iframe
                                    className="position-relative w-100 h-100"
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7003.931319813107!2d77.043490025!3d28.459497608350824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1914b8f8a6fb%3A0x9dc3abf9b1a1c272!2sGurugram%2C%20Haryana!5e0!3m2!1sen!2sin!4v1730730000000!5m2!1sen!2sin"
                                    frameBorder="0"
                                    style={{ minHeight: "450px", border: "0" }}
                                    allowFullScreen=""
                                    aria-hidden="false"
                                    tabIndex="0"
                                    title="Office Location"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Virtual Meet Section */}
            <div className="container py-5 fade-up">
                <div className="text-center mb-5">
                    <h2 className="fw-bold text-dark mb-3 section-title" style={{ color: 'var(--heading)' }}>
                        Prefer a Personal Touch?
                    </h2>
                    <p className="text-muted mx-auto section-subtitle" style={{ color: 'var(--text)' }}>
                        If you're more comfortable talking to someone directly, we understand.
                        Money is personal — and so is our approach. You can call or WhatsApp us
                        anytime during business hours.
                    </p>
                    <p className="fs-5">
                        <FontAwesomeIcon icon={faComments} style={{ color: 'var(--primary)' }} className="me-2" />
                        <a
                            href="https://wa.me/917011468336"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fw-bold text-decoration-none"
                            style={{ color: 'var(--primary)' }}
                        >
                            Click here
                        </a>{" "}
                        to start a WhatsApp chat with our advisor now.
                    </p>
                </div>

                {/* Add card layout for virtual meeting options */}
                <div className="row justify-content-center g-4">
                    <div className="col-md-4">
                        <div className="info-card text-center p-4 h-100" style={{ backgroundColor: 'var(--white)', border: '1px solid var(--light-bg)' }}>
                            <div className="fs-1 mb-3">📞</div>
                            <h5 className="fw-bold" style={{ color: 'var(--heading)' }}>Phone Call</h5>
                            <p style={{ color: 'var(--text)' }}>
                                Speak directly with your personal financial advisor — anytime,
                                anywhere.
                            </p>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="info-card text-center p-4 h-100" style={{ backgroundColor: 'var(--white)', border: '1px solid var(--light-bg)' }}>
                            <div className="fs-1 mb-3">💻</div>
                            <h5 className="fw-bold" style={{ color: 'var(--heading)' }}>Google Meet / Zoom</h5>
                            <p style={{ color: 'var(--text)' }}>
                                Join a secure online consultation with detailed portfolio review and
                                goal mapping.
                            </p>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="info-card text-center p-4 h-100" style={{ backgroundColor: 'var(--white)', border: '1px solid var(--light-bg)' }}>
                            <div className="fs-1 mb-3">💬</div>
                            <h5 className="fw-bold" style={{ color: 'var(--heading)' }}>WhatsApp Video or Chat</h5>
                            <p style={{ color: 'var(--text)' }}>
                                Quick, convenient, and personal — perfect for short questions or
                                follow-ups.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* After Contact Section */}
            <div className="container-fluid py-5 fade-up" style={{ backgroundColor: 'var(--light-bg)' }}>
                <div className="container text-center">
                    <h2 className="fw-bold mb-3 section-title" style={{ color: 'var(--heading)' }}>
                        What Happens After You Contact Us?
                    </h2>
                    <div className="row justify-content-center g-4">
                        {[
                            {
                                step: "1",
                                title: "Quick Acknowledgment",
                                desc: "You'll receive a reply within 24 hours confirming your inquiry.",
                            },
                            {
                                step: "2",
                                title: "Discovery Call",
                                desc: "We'll understand your goals, challenges, and unique financial needs.",
                            },
                            {
                                step: "3",
                                title: "Tailored Suggestions",
                                desc: "We design customized plans aligned with your profile and objectives.",
                            },
                            {
                                step: "4",
                                title: "Zero-Pressure Guidance",
                                desc: "You decide what's next — we're here to support, not to push.",
                            },
                        ].map((item, index) => (
                            <div key={index} className="col-md-6 col-lg-3">
                                <div className="trust-card text-center h-100 py-4 px-3" style={{ backgroundColor: 'var(--white)' }}>
                                    <div
                                        className="rounded-circle text-white d-inline-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            fontSize: "1.5rem",
                                            backgroundColor: 'var(--primary)'
                                        }}
                                    >
                                        {item.step}
                                    </div>
                                    <h5 className="fw-bold" style={{ color: 'var(--heading)' }}>{item.title}</h5>
                                    <p className="small" style={{ color: 'var(--text)' }}>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-4" style={{ color: 'var(--text)' }}>
                        Whether you choose to work with us long-term or just need one-time
                        advice — you'll always walk away with value and clarity.
                    </p>
                </div>
            </div>

            <div className="container py-5">
                <div className="text-center mb-4">
                    <motion.h2
                        className="fw-bold"
                        style={{ color: 'var(--heading)' }}
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        Why Clients Trust SAG Financials
                    </motion.h2>
                    <motion.p
                        style={{ color: 'var(--text)' }}
                        initial={{ opacity: 0, y: -10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        Experience. Integrity. Results. That's what sets us apart.
                    </motion.p>
                </div>

                <div className="row g-4">
                    {[
                        "22+ Years of Experience – Expertise built across top financial institutions.",
                        "Certified & Ethical – AMFI, IRDA, and NISM certified advisors.",
                        "Client-First Approach – We focus on what's right for you, not what sells.",
                        "Award-Winning Track Record – Recognized by Aditya Birla, IDBI Federal & more.",
                        "100% Digital, 100% Human – Convenience meets personal support.",
                    ].map((text, i) => (
                        <div key={i} className="col-md-6 col-lg-4">
                            <motion.div
                                className="p-4 border rounded h-100 shadow-sm"
                                style={{ backgroundColor: 'var(--light-bg)' }}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                whileHover={{
                                    y: -5,
                                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
                                }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.1,
                                    type: "spring",
                                    stiffness: 100
                                }}
                                viewport={{ once: true }}
                            >
                                <motion.span
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{
                                        duration: 0.4,
                                        delay: i * 0.1 + 0.3,
                                        type: "spring"
                                    }}
                                    viewport={{ once: true }}
                                >
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        style={{ color: 'var(--primary)' }}
                                        className="me-2"
                                    />
                                </motion.span>
                                <span style={{ color: 'var(--text)' }}>{text}</span>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>
        </> 
    );
};

export default Contact;
